import { EnvNames } from "~/constants/env-names";

type Config<T = unknown> = Record<string, T>;

export type Dictionary<T = unknown> = Record<string, T> & {
  default?: T;
};

export interface Protected<ST = unknown, CT = ST> {
  server?: ST;
  client?: CT;
}

export enum NewRelicEnvs {
  dev = "dev",
  prod = "prod",
}

export interface FeatureFlagList {
  bloomreachBruid?: FeatureFlag;
  bloomreachRefUrl?: FeatureFlag;
  bloomreachBruidSrp?: FeatureFlag;
  bloomreachRefUrlSrp?: FeatureFlag;
  bloomreachBruidPlp?: FeatureFlag;
  bloomreachBruidClp?: FeatureFlag;
  bloomreachRefUrlPlp?: FeatureFlag;
  bloomreachRefUrlClp?: FeatureFlag;
  bloomreachBruidRbp?: FeatureFlag;
  bloomreachRefUrlRbp?: FeatureFlag;
  bloomreachBruidPdpSimilarItems?: FeatureFlag;
  bloomreachRefUrlPdpSimilarItems?: FeatureFlag;
  changeInfoProfile?: FeatureFlag;
  shoppingList?: FeatureFlag;
  clickAndCollect?: FeatureFlag;
  newsletterSignUp?: FeatureFlag;
  preferredStore?: FeatureFlag;
  doubleOptIn?: FeatureFlag;
  myAccount?: FeatureFlag;
  contactUsPage?: FeatureFlag;
  pricePerUnit?: FeatureFlag;
  PDPLongDescription?: FeatureFlag;
  newCategoryFormat?: FeatureFlag;
  dynamicYield?: FeatureFlag;
  displayRegion?: FeatureFlag;
  recentlyViewedProducts?: FeatureFlag;
  customLocationSearch?: FeatureFlag;
  loqateLocationSearch?: FeatureFlag;
  geolocation?: FeatureFlag;
  abandonedCart?: FeatureFlag;
  miniAddToBagCheckoutAndViewBagCTA?: FeatureFlag;
  dyCampaignABMiniAddToBagCheckoutAndViewbagCTA?: FeatureFlag;
  applePayQuickBuy?: FeatureFlag;
  dyCampaignABApplePayExpressCheckout?: FeatureFlag;
  isDynamicSwiperExpressCheckout?: FeatureFlag;
  productDataSheetPage?: FeatureFlag;
  crossSellFeatureBasketQuickView?: FeatureFlag;
  agecBanner?: FeatureFlag;
  comingSoon?: FeatureFlag;
  comingSoonPhase2?: FeatureFlag;
  smartBanner?: FeatureFlag;
  clearancePricing?: FeatureFlag;
  pdpStoreSelection?: FeatureFlag;

  //DY campaigns
  dyCampaignPoc?: FeatureFlag;
  dyCampaignRVP?: FeatureFlag;
  dyCampaignUgcPdp?: FeatureFlag;
  dyCampaignLoq?: FeatureFlag;
  dyCampaignGeo?: FeatureFlag;
  dyCampaignSplitterHome?: FeatureFlag;
}

export interface WithFeatureFlags {
  featureFlagList?: FeatureFlagList;
}

export interface ClientConfig extends Config {
  envName?: EnvNames;
  testKey?: string;
  technical?: {
    debug?: string;
    testIdsAreEnabled?: string;
    minifyJsIsEnabled?: string;
    minifyCssIsEnabled?: string;
  };
  azure?: {
    adB2C?: {
      clientId?: string;
      tenant?: string;
      signUpSignInFlow?: string;
      changePasswordFlow?: string;
      resetPasswordFlow?: string;
      customDomains?: {
        signUpSignIn?: string;
        changePassword?: string;
        resetPassword?: string;
      };
    };
  };
  gtm?: {
    id?: string;
    baseUrl?: string;
  };
  oneTrust?: {
    id?: string;
  };
  facebookAppId?: string;
  bloomReachPixel?: {
    domainKey?: string;
    id?: string;
    testData?: string;
    tms?: string;
    crossSellWidget?: {
      id?: string;
    };
    catalogName?: string;
  };
  bloomReach?: {
    recentlyViewedWidget?: {
      id?: string;
      brApiUrl?: string;
      maximumProductsInCarousel?: number;
      minimumProductsInCarousel?: number;
    };
  };
  bloomreachSuggestion?: {
    baseUrl?: string;
    accountId?: string;
    domainKey?: string;
    catalogViews?: string;
    domainName?: string;
  };
  evergage?: {
    anonymousUserId?: string;
    cookieDomain?: string;
    snippetUrl?: string;
  };
  loqate?: {
    clientId?: string;
  };
  socialNativeWidget?: {
    apiKey?: string;
    script?: string;
  };
  ugcStoryStream?: {
    script?: string;
  };
  stlStoryStream?: {
    script?: string;
  };
  salesforceForm?: {
    parameters?: string;
    preferredStoreId?: string;
    accountCreationLeadParameters?: string;
  };
  gaTrackingId?: string;
  newRelic?: {
    env?: NewRelicEnvs;
  };
  amplience?: {
    robotsTxtDeliveryKey?: string;
    globalLayoutAndNavigationTTLSeconds: string;
  };
  apiBaseUrl?: string;
  expressCheckoutApiUrl?: string;
  expressCheckoutKountClientID?: string;
  expressCheckoutKountEnv?: string;
  expressCheckoutKountIsDebugEnable?: string;
  frontEndBaseUrl?: string;
  cookieDomain?: string;
  contactUsForm?: {
    parameters?: string;
  };
  thanksForYourOrderPage?: {
    timeToExpire?: string;
  };
  resolverVersion?: string;
  dynamicYield?: {
    id?: string;
    clientApiKey?: string;
    euClientBaseUrl?: string;
    euClientReportCollectUrl?: string;
    engagementEuClientBaseUrl?: string;
  };
  storeSearch?: {
    storeSearchDelay?: string;
    storeSearchType?: string;
    loqateSearchUrl?: string;
    loqateLocationTypes?: string;
  };
  googleMaps?: {
    apiKey?: string;
    searchWaitingTime?: number;
  };
  exceptionalLinks?: string;
  earlyHints?: {
    thirdPartyLinks?: string;
  };
}

export interface ServerOnlyConfig extends Config {
  testKey?: string;
  apiBaseUrl?: string;
  configTtl?: number;
}

export type ClientAvailableConfigData = ClientConfig & WithFeatureFlags;

export type AppConfig = ClientConfig & ServerOnlyConfig;

export type AllPossibleConfigData = AppConfig & WithFeatureFlags;

export enum AzureConfiguratorEvent {
  INIT = "INIT",
  UPDATE = "UPDATE",
  SET = "SET",
}

export interface FeatureFlag {
  id: string;
  description: string;
  enabled: boolean;
  conditions: { clientFilters: unknown };
  label: string | null;
  [key: string]: unknown;
}
export interface AppSettings {
  config: { [key: string]: { value: string; key: string } };
  featureFlags: { [key: string]: FeatureFlag };
}

export interface AppSettingsForLocale {
  [key: string]: AppSettings;
}

export interface FeatureManagement {
  feature_flags: FeatureFlag[];
}
